// A small bus between the menus, the panels and the board (slices 06 and
// 08): "fit to screen" is a tool that lives in the top bar (design/08 §4),
// and a delete may start on the canvas (the Delete key) or in a panel but
// is confirmed in one place; the board is driven outside React's render
// loop, so the menu asks and the board acts.
type Handler = () => void;
type DeleteHandler = (ids: string[]) => void;
type RevealHandler = (ids: string[], reveal: boolean) => void;
type FocusHandler = (id: string) => void;
type GoToHandler = (point: { x: number; y: number }) => void;
export interface BoardViewState { view: { x: number; y: number; zoom: number }; viewport: { width: number; height: number }; extent: { width: number; height: number } }
type ViewHandler = (v: BoardViewState) => void;
export interface FocusHover { id: string; at: { x: number; y: number } }
type HoverHandler = (h: FocusHover | null) => void;
/**
 * Where the token's menu opens (issue 812): the thing, and the point on screen. `selected` is what was selected before
 * the press that opened it selected the thing, so Target can hand it back (issue 1423).
 */
export interface TokenMenuAt { id: string; x: number; y: number; selected?: string[] }
type TokenMenuHandler = (at: TokenMenuAt) => void;
type ToolHandler = (tool: string) => void;
/** A ping that arrived (issue 1039): where, by whom. */
export interface PingAt { x: number; y: number; by: string }
type PingHandler = (p: PingAt) => void;
const fitHandlers = new Set<Handler>();
const deleteHandlers = new Set<DeleteHandler>();
const revealHandlers = new Set<RevealHandler>();
const focusHandlers = new Set<FocusHandler>();
const goToHandlers = new Set<GoToHandler>();
const viewHandlers = new Set<ViewHandler>();
const hoverHandlers = new Set<HoverHandler>();
/** The token under the pointer (issue 1127): its id and where the pointer is on the screen; null when it leaves. */
/** `touch`: opened by a long press (qa-runs#1160); `from`: the token selected before it, which the long press replaced. */
export interface TokenHover { id: string; at: { x: number; y: number }; touch?: true; from?: string }
type TokenHoverHandler = (h: TokenHover | null) => void;
const tokenHoverHandlers = new Set<TokenHoverHandler>();
const tokenMenuHandlers = new Set<TokenMenuHandler>();
const toolHandlers = new Set<ToolHandler>();
const clearFightHandlers = new Set<Handler>();
const pingHandlers = new Set<PingHandler>();
/** A compendium result dropped on the board (issue 1045): what, and where in board pixels (null before the board settled). */
export interface EntryDrop { entry: { segment: string; slug: string; name: string }; at: { x: number; y: number } | null }
type EntryDropHandler = (d: EntryDrop) => void;
const entryDropHandlers = new Set<EntryDropHandler>();
let lastView: BoardViewState | null = null;

export const boardBus = {
  /** A ping arrived (issue 1039): the board draws it where it was made, in its maker's colour, fading. */
  onPinged(handler: PingHandler): () => void {
    pingHandlers.add(handler);
    return () => { pingHandlers.delete(handler); };
  },
  pinged(p: PingAt): void {
    for (const h of pingHandlers) h(p);
  },
  onFit(handler: Handler): () => void {
    fitHandlers.add(handler);
    return () => { fitHandlers.delete(handler); };
  },
  fit(): void {
    for (const h of fitHandlers) h();
  },
  /** Focus on a thing (design/08 §2, design/16 §2): a stone clicked centres the board on its creature. */
  onFocus(handler: FocusHandler): () => void {
    focusHandlers.add(handler);
    return () => { focusHandlers.delete(handler); };
  },
  focus(id: string): void {
    for (const h of focusHandlers) h(id);
  },
  /** Travel to a point on the board (slice 13): the edge arrow's click, "go to" in a list. Only your own view moves. */
  onGoTo(handler: GoToHandler): () => void {
    goToHandlers.add(handler);
    return () => { goToHandlers.delete(handler); };
  },
  goTo(point: { x: number; y: number }): void {
    for (const h of goToHandlers) h(point);
  },
  /** The board's camera, published by the canvas on every settle, so the edge arrows can be laid out in React. */
  onView(handler: ViewHandler): () => void {
    viewHandlers.add(handler);
    if (lastView) handler(lastView);
    return () => { viewHandlers.delete(handler); };
  },
  /** The centre of this screen's view in board pixels (issue 848: where a placed character lands), or null before the board has settled. */
  viewCentre(): { x: number; y: number } | null {
    if (!lastView || !(lastView.view.zoom > 0)) return null;
    const { view, viewport } = lastView;
    return { x: view.x + viewport.width / (2 * view.zoom), y: view.y + viewport.height / (2 * view.zoom) };
  },
  /** A point on this screen's board, relative to the board's top left, in board pixels; null before the board has settled. */
  pointAt(sx: number, sy: number): { x: number; y: number } | null {
    if (!lastView || !(lastView.view.zoom > 0)) return null;
    const { view } = lastView;
    return { x: view.x + sx / view.zoom, y: view.y + sy / view.zoom };
  },
  /** A compendium result dropped on the board (issue 1045): the board page opens the Place dialog. */
  onEntryDropped(handler: EntryDropHandler): () => void {
    entryDropHandlers.add(handler);
    return () => { entryDropHandlers.delete(handler); };
  },
  entryDropped(d: EntryDrop): void {
    for (const h of entryDropHandlers) h(d);
  },
  viewChanged(v: BoardViewState): void {
    lastView = v;
    for (const h of viewHandlers) h(v);
  },
  /** The focus under the hand, for its note (decision 33), or null. */
  onHoverFocus(handler: HoverHandler): () => void {
    hoverHandlers.add(handler);
    return () => { hoverHandlers.delete(handler); };
  },
  hoverFocus(h: FocusHover | null): void {
    for (const x of hoverHandlers) x(h);
  },
  /** A token's hover card follows this (issue 1127). */
  onHoverToken(handler: TokenHoverHandler): () => void {
    tokenHoverHandlers.add(handler);
    return () => { tokenHoverHandlers.delete(handler); };
  },
  hoverToken(h: TokenHover | null): void {
    for (const x of tokenHoverHandlers) x(h);
  },
  /** Whoever renders the confirmation listens; the canvas and the panel both ask this way. */
  onDelete(handler: DeleteHandler): () => void {
    deleteHandlers.add(handler);
    return () => { deleteHandlers.delete(handler); };
  },
  requestDelete(ids: string[]): void {
    for (const h of deleteHandlers) h(ids);
  },
  /** Reveal or hide several as one event (slice 09): from the canvas (a key) or a panel, acted on in one place with the numbered-group reminder. */
  onReveal(handler: RevealHandler): () => void {
    revealHandlers.add(handler);
    return () => { revealHandlers.delete(handler); };
  },
  requestReveal(ids: string[], reveal: boolean): void {
    for (const h of revealHandlers) h(ids, reveal);
  },
  /** The token's menu (issue 812): the right button on a thing, or the Menu key with one selected. */
  onTokenMenu(handler: TokenMenuHandler): () => void {
    tokenMenuHandlers.add(handler);
    return () => { tokenMenuHandlers.delete(handler); };
  },
  openTokenMenu(at: TokenMenuAt): void {
    for (const h of tokenMenuHandlers) h(at);
  },
  /** Show a tool (issue 812: "Properties" from the token's menu): the shell puts it on a free bar, or asks where. */
  onShowTool(handler: ToolHandler): () => void {
    toolHandlers.add(handler);
    return () => { toolHandlers.delete(handler); };
  },
  showTool(tool: string): void {
    for (const h of toolHandlers) h(tool);
  },
  /** Offer to clear what a fight left on the board (issue 812): from the Combat menu or the tracker, after the fight. */
  onClearFight(handler: Handler): () => void {
    clearFightHandlers.add(handler);
    return () => { clearFightHandlers.delete(handler); };
  },
  requestClearFight(): void {
    for (const h of clearFightHandlers) h();
  },
};
